import React, { useState } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import {
    Container,
    Row,
    Col,
    Card,
    Button,
    Form,
    InputGroup,
    Spinner,
    Modal,
    ProgressBar,
} from 'react-bootstrap'
import { SEARCH_RESOURCES_CENTER } from '../../gql/queries/resourceCenterQuery'
import { CampaignMediaUploadModal } from './components/CampaignMediaUploadModal'
import { prependBackendUrl } from '../../gql/client'
import { toast } from 'react-toastify'
import { DELETE_RESOURCE_CENTER } from '../../gql/mutations/resourceCenterMutations'
import { useIntl, FormattedMessage } from 'react-intl'
import { CAMPAIGN_MESSAGES as M } from './campaignMessages'

/** El rule marca los template literals dentro de `<style>`: se iza a const (guía §17.18). */
const MEDIA_CARD_STYLES = `
                .media-card-item:hover {
                    transform: translateY(-5px);
                    transition: transform 0.2s;
                }
                .media-card-overlay {
                    position: absolute;
                    top: 0;
                    right: 0;
                    padding: 8px;
                    opacity: 0;
                    transition: opacity 0.2s;
                    background: rgba(0,0,0,0.1);
                    width: 100%;
                    height: 100%;
                    display: flex;
                    align-items: flex-start;
                    justify-content: flex-end;
                }
                .media-card-item:hover .media-card-overlay {
                    opacity: 1;
                }
            `

export const CampaignsMediaPage = () => {
    const intl = useIntl()
    const [searchTerm, setSearchTerm] = useState('')
    const [showUploadModal, setShowUploadModal] = useState(false)
    const [selectedImage, setSelectedImage] = useState<any>(null)
    const [showDeleteModal, setShowDeleteModal] = useState(false)

    // Query to fetch images (Resource Type: 'image', only campaign media)
    const { data, loading, refetch } = useQuery(SEARCH_RESOURCES_CENTER, {
        variables: {
            name: searchTerm,
            resourceType: 'image',
            isActive: true,
            isCampaignMedia: true, // Only show campaign media files
        },
        fetchPolicy: 'network-only',
    })

    const [deleteResource] = useMutation(DELETE_RESOURCE_CENTER)

    const resources = data?.resourcesCenterAdvancedSearch || []

    const images = resources.filter((r: any) => {
        const resourceType = r.resourceType?.toLowerCase()
        return (
            resourceType === 'image' ||
            (r.file && /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(r.file))
        )
    })

    const getUrl = (resource: any) => {
        if (resource.url) return resource.url
        if (resource.file) {
            // Si la URL ya es absoluta (S3), no agregar prefijo del backend
            if (resource.file.startsWith('http://') || resource.file.startsWith('https://')) {
                return resource.file
            }
            return prependBackendUrl(resource.file)
        }
        return ''
    }

    const copyToClipboard = (url: string) => {
        navigator.clipboard.writeText(url)
        toast.info(intl.formatMessage(M.toastLinkCopied))
    }

    const handleDeleteClick = (img: any) => {
        setSelectedImage(img)
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (!selectedImage) return

        try {
            await deleteResource({ variables: { id: parseInt(selectedImage.id) } })
            toast.success(intl.formatMessage(M.toastImageDeleted))
            refetch()
            setShowDeleteModal(false)
            setSelectedImage(null)
        } catch (error) {
            console.error(error)
            toast.error(intl.formatMessage(M.toastImageDeleteError))
        }
    }

    return (
        <div className='campaigns-media-page py-4 px-5'>
            <div className='d-flex justify-content-between align-items-center mb-4'>
                <div>
                    <h1 className='h3 mb-1'><FormattedMessage {...M.mediaTitle} /></h1>
                    <p className='text-muted'>
                        <FormattedMessage {...M.mediaSubtitle} />
                    </p>
                </div>
                <Button variant='primary' onClick={() => setShowUploadModal(true)}>
                    <i className='bi bi-cloud-upload me-2'></i> <FormattedMessage {...M.mediaUploadNew} />
                </Button>
            </div>

            {/* Filter Bar */}
            <Card className='mb-4 shadow-sm'>
                <Card.Body className='p-3'>
                    <Row className='align-items-center'>
                        <Col md={6}>
                            <InputGroup>
                                <InputGroup.Text className='bg-white border-end-0'>
                                    <i className='bi bi-search text-muted'></i>
                                </InputGroup.Text>
                                <Form.Control
                                    className='border-start-0 ps-0'
                                    placeholder={intl.formatMessage(M.mediaSearch)}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </InputGroup>
                        </Col>
                        {/* Summary Stats */}
                        <Col md={6} className='text-md-end text-muted small'>
                            <FormattedMessage {...M.mediaShowing} values={{count: images.length}} />
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Gallery Grid */}
            {loading ? (
                <div className='text-center py-5'>
                    <Spinner animation='border' variant='primary' />
                    <p className='mt-2 text-muted'><FormattedMessage {...M.mediaLoading} /></p>
                </div>
            ) : images.length === 0 ? (
                <div className='text-center py-5 bg-white rounded shadow-sm'>
                    <i className='bi bi-images fs-1 text-muted mb-3 d-block'></i>
                    <h5><FormattedMessage {...M.mediaEmpty} /></h5>
                    <p className='text-muted'><FormattedMessage {...M.mediaEmptyHint} /></p>
                    <Button variant='outline-primary' onClick={() => setShowUploadModal(true)}>
                        <FormattedMessage {...M.mediaUploadNow} />
                    </Button>
                </div>
            ) : (
                <Row xs={1} sm={2} md={3} lg={4} xl={5} className='g-4'>
                    {images.map((img: any) => {
                        const src = getUrl(img)
                        return (
                            <Col key={img.id}>
                                <Card className='h-100 shadow-sm border-0 media-card-item'>
                                    <div
                                        className='position-relative bg-light d-flex align-items-center justify-content-center overflow-hidden'
                                        style={{ height: '180px' }}
                                    >
                                        {src ? (
                                            <img
                                                src={src}
                                                alt={img.name}
                                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                            />
                                        ) : (
                                            <i className='bi bi-image text-muted fs-1'></i>
                                        )}
                                        {/* Overlay Actions */}
                                        <div className='media-card-overlay'>
                                            <Button
                                                variant='light'
                                                size='sm'
                                                className='m-1 shadow-sm'
                                                title={intl.formatMessage(M.mediaCopyLink)}
                                                onClick={() => copyToClipboard(src)}
                                            >
                                                <i className='bi bi-link-45deg'></i>
                                            </Button>
                                            <Button
                                                variant='danger'
                                                size='sm'
                                                className='m-1 shadow-sm'
                                                title={intl.formatMessage(M.delete)}
                                                onClick={() => handleDeleteClick(img)}
                                            >
                                                <i className='bi bi-trash'></i>
                                            </Button>
                                        </div>
                                    </div>
                                    <Card.Body className='p-3'>
                                        <h6 className='text-truncate fw-bold mb-1' title={img.name}>
                                            {img.name}
                                        </h6>
                                        <div className='d-flex justify-content-between align-items-center'>
                                            <small className='text-muted'>
                                                {new Date(img.created).toLocaleDateString()}
                                            </small>
                                            <small className='badge bg-light text-dark border'>
                                                <FormattedMessage {...M.mediaIdLabel} values={{id: img.id}} />
                                            </small>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        )
                    })}
                </Row>
            )}

            {/* Upload Modal (Simplified) */}
            {showUploadModal && (
                <CampaignMediaUploadModal
                    isOpen={showUploadModal}
                    onClose={() => setShowUploadModal(false)}
                    refetch={refetch}
                />
            )}

            <style>{MEDIA_CARD_STYLES}</style>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton className='border-0 pb-0'>
                    <Modal.Title><FormattedMessage {...M.mediaDeleteTitle} /></Modal.Title>
                </Modal.Header>
                <Modal.Body className='pt-3 pb-4'>
                    <div className='alert alert-warning d-flex align-items-start' role='alert'>
                        <i className='bi bi-exclamation-circle me-2 fs-4'></i>
                        <div>
                            <FormattedMessage
                                {...M.mediaDeleteBody}
                                values={{
                                    name: selectedImage?.name ?? '',
                                    b: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
                                }}
                            />
                            <div className='mt-2 small'>
                                <FormattedMessage {...M.mediaDeleteHint} />
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className='border-0 justify-content-end'>
                    <Button variant='light' onClick={() => setShowDeleteModal(false)}>
                        <FormattedMessage {...M.cancel} />
                    </Button>
                    <Button variant='danger' onClick={confirmDelete}>
                        <FormattedMessage {...M.delete} />
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    )
}
